/**
 * Shared labelled on/off row used across the admin settings tabs.
 *
 * Extracted from FeaturesTab so other setting sections (e.g. the ERP writeback
 * rules) render identical controls instead of re-implementing the layout.
 */
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';

export interface FeatureToggleProps {
  icon: React.ReactNode;
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Renders the row read-only. Used where the viewer lacks permission. */
  disabled?: boolean;
}

/**
 * A single settings toggle with icon, label and explanatory copy.
 *
 * @param icon - Lucide icon element shown to the left of the label
 * @param label - Short control name
 * @param description - Sentence explaining what on/off means, and the default
 * @param checked - Current value
 * @param onChange - Called with the new value
 * @param disabled - When true the switch is not interactive
 * @returns The toggle row
 */
export function FeatureToggle({
  icon,
  label,
  description,
  checked,
  onChange,
  disabled = false,
}: FeatureToggleProps) {
  return (
    <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors">
      <div className="flex items-center gap-3">
        {icon}
        <div>
          <Label className="text-base font-medium">{label}</Label>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} />
    </div>
  );
}
